import moment from "moment";
import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { Controller, useForm } from "react-hook-form";
import { states } from "js/placeholderData/states";
import { validatePolicyNumber } from "service/policy/validatePolicyNumber";

import { isFlagEnabled, isPolicyInactiveByPolicyStatus } from "js/utilities";
import { useTranslation } from "react-i18next";

import SSNInput from "components/General/Form/SSNInput";
import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import RELEASING_AutoCompleteComboBox from "components/General/AutoCompleteComboBox/RELEASING_AutoCompleteComboBox";
import Badge from "components/General/Badge";

export default function FACStepOne({
  setSelectedPolicy,
  formStep,
  setFormStep,
  setFACFormData,
}) {
  const [policyFetching, setPolicyFetching] = useState(false);
  let debounceFunction; //timer identifier
  let debounceTimer = 1500; //time in ms, 1.5 seconds for example
  const [policyNotFound, setPolicyNotFound] = useState();
  const [isDateOfBirthValid, setIsDateOfBirthValid] = useState(true);
  const [isDateOfDeathValid, setIsDateOfDeathValid] = useState(true);
  const [policyDetails, setPolicyDetails] = useState([]);
  const [selectedState, setSelectedState] = useState({
    value: "all",
  });
  const [isDisabled, setIsDisabled] = useState(true);
  const [isInactivePolicy, setIsInactivePolicy] = useState(false);
  const uuid = uuidv4();
  const { t } = useTranslation();
  const {
    register: FACStepOneRegister,
    setValue,
    getValues,
    watch,
    handleSubmit: FACStepOneHandleSubmit,
    formState: { errors },
    control,
  } = useForm();

  const onSubmit = (data) => {
    if (!policyNotFound && getValues("policyNumber")) {
      // screenScrollTo();
      setFormStep(2);
      setFACFormData((currentFormData) => {
        return {
          ...currentFormData,
          ...data,
        };
      });
    } else {
      setIsInactivePolicy(false);
      setPolicyNotFound(t("UnauthFACStep-contract-not-found-error-message"));
    }
  };

  const maritalStatus = watch("marital");
  const foreignDeath = watch("foreign");

  const flagEnable = isFlagEnabled("ENABLE_OWNER_NOT_INDIVIDUAL");

  const getFieldName = () => {
    return flagEnable ? "ownerFullName" : "ownerLastName";
  };

  const getLabelName = () => {
    return flagEnable
      ? `${t("UnauthFACStep-contract-owner-name")} *`
      : `${t("UnauthFACStep-contract-owner-last-name")} *`;
  };

  const getPlaceholderText = () => {
    return flagEnable
      ? t("UnauthFACStep-contract-owner-name")
      : t("UnauthFACStep-contract-owner-last-name");
  };

  const getLabelError = () => {
    return flagEnable
      ? t("UnauthFACStep-required-field")
      : t("UnauthFACStep-enter-last-name");
  };

  return (
    <>
      {formStep === 1 && (
        <EverlakeLoadingOverlay
          isLoading={policyFetching}
          loadingText={t("FACStep-one-loadingtext")}
        >
          <form onSubmit={FACStepOneHandleSubmit(onSubmit)}>
            <p
              id={`${uuid}-pn-note`}
              className="text-xs text-red-600  font-bold mb-8 italic"
            >
              {t("FACStep-one-subtitle")}
            </p>
            <hr />
            <h2 className="flex flex-grow  font-body font-bold text-lg mb-2 tracking-tight">
              {t("UnauthFACStep-contract-information")}
            </h2>
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              <div
                className={`grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-${
                  flagEnable ? "[2fr,2fr]" : "[1fr,1fr,1fr]"
                }`}
              >
                <div>
                  <label
                    htmlFor={`${uuid}-policyNumber`}
                    className="block mb-1"
                  >
                    {t("UnauthFACStep-contract-number")}
                  </label>
                  <div className="relative select-none">
                    <input
                      id={`${uuid}-policyNumber`}
                      className={`uppercase placeholder:normal-case ${
                        isInactivePolicy ? "border-red-700" : ""
                      }`}
                      type="text"
                      //disabled={watch("unknownpolicyNumber")}
                      placeholder={t("FACStep-one-contract-placeholder")}
                      {...FACStepOneRegister("policyNumber", {
                        required: "Please enter a valid policy number.",
                      })}
                      onKeyUp={() => {
                        clearTimeout(debounceFunction);
                        debounceFunction = setTimeout(async () => {
                          if (
                            getValues("policyNumber") &&
                            getValues("policyNumber") !== ""
                          ) {
                            setPolicyFetching(true);
                            let queryString = `ValidateInsured=false&ContractId=${getValues(
                              "policyNumber"
                            )
                              .toUpperCase()
                              .trim()}`;
                            setIsInactivePolicy(false);
                            await validatePolicyNumber(queryString)
                              .then((response) => {
                                setIsDisabled(true);
                                if (!response?.contractId) {
                                  setPolicyNotFound(
                                    t(
                                      "UnauthFACStep-contract-not-found-error-message"
                                    )
                                  );
                                  setPolicyDetails([]);
                                } else if (
                                  isPolicyInactiveByPolicyStatus(
                                    response?.status
                                  )
                                ) {
                                  setIsInactivePolicy(true);
                                  setPolicyNotFound(
                                    t(
                                      "UnauthFACStep-inactive-contract-error-message"
                                    )
                                  );
                                  setPolicyDetails([]);
                                } else {
                                  setIsDisabled(false);
                                  setSelectedPolicy(response?.contractId);
                                  setPolicyDetails(response?.parties);
                                  setPolicyNotFound(undefined);
                                }
                              })
                              .catch((err) => {
                                setPolicyNotFound(
                                  t(
                                    "UnauthFACStep-contract-not-found-error-message"
                                  )
                                );
                                setIsDisabled(true);
                                setPolicyDetails([]);
                              });
                            setPolicyFetching(false);
                          } else {
                            setPolicyNotFound(
                              t(
                                "UnauthFACStep-contract-not-found-error-message"
                              )
                            );
                            setIsDisabled(true);
                            setPolicyDetails([]);
                          }
                        }, debounceTimer);
                      }}
                      onKeyDown={() => {
                        clearTimeout(debounceFunction);
                      }}
                    />
                    {isInactivePolicy && (
                      <span className={`absolute inset-y-0 right-1 flex pr-4`}>
                        <Badge iconOnly={true} status={"problem"} />
                      </span>
                    )}
                  </div>

                  {policyNotFound && (
                    <FormInputError
                      errorMessage={policyNotFound}
                      isOnlyText={isInactivePolicy ? true : false}
                    />
                  )}
                </div>
                {!flagEnable && (
                  <div>
                    <label htmlFor={`${uuid}-owner-fn`} className="block mb-1">
                      {t("UnauthFACStep-owner-nm")}
                    </label>
                    <input
                      id={`${uuid}-owner-fn`}
                      type="text"
                      disabled={isDisabled}
                      placeholder={t("FACStep-one-owner-placeholder")}
                      {...FACStepOneRegister("ownerFirstName", {
                        required: "Enter First Name.",
                      })}
                    />
                    <FormInputError
                      id={`${uuid}-owner-fn-error`}
                      errorMessage={errors.ownerFirstName?.message}
                    />
                  </div>
                )}

                <div>
                  <label
                    htmlFor={`${uuid}-owner-${flagEnable ? "fullname" : "ln"}`}
                    className="block mb-1"
                  >
                    {getLabelName()}
                  </label>
                  <input
                    id={`${uuid}-owner-${flagEnable ? "fullname" : "ln"}`}
                    type="text"
                    disabled={isDisabled}
                    placeholder={getPlaceholderText()}
                    {...FACStepOneRegister(getFieldName(), {
                      required: getLabelError(),
                    })}
                  />
                  <FormInputError
                    id={`${uuid}-owner-${
                      flagEnable ? "fullname-error" : "ln-error"
                    }`}
                    errorMessage={
                      flagEnable
                        ? errors.ownerFullName?.message
                        : errors.ownerLastName?.message
                    }
                  />
                </div>
              </div>
            </div>

            <h2 className="flex flex-grow font-body font-bold text-lg mb-2 mt-8 tracking-tight">
              {t("UnauthFACStep-deceased-information")}
            </h2>
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr,1fr]">
                <div>
                  <label htmlFor={`${uuid}-fn`} className="block mb-1">
                    {t("UnauthFACStep-fn")}
                  </label>
                  <input
                    id={`${uuid}-fn`}
                    type="text"
                    disabled={
                      watch("sameAsInsured") ||
                      watch("unKnownFullName") ||
                      isDisabled
                    }
                    placeholder={t("FACStep-one-fn-placeholder")}
                    {...FACStepOneRegister("firstName")}
                  />
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-normal">
                      <input
                        id={`${uuid}-unKnownFirstName`}
                        type="checkbox"
                        value="unKnownFirstName"
                        disabled={isDisabled}
                        className="mr-1"
                        {...FACStepOneRegister("unKnownFirstName")}
                      />
                      {t("UnauthFACStep-uknown")}
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor={`${uuid}-ln`} className="block mb-1">
                    {t("UnauthFACStep-ln")}
                  </label>
                  <input
                    id={`${uuid}-ln`}
                    type="text"
                    placeholder={t("FACStep-one-ln-placeholder")}
                    disabled={
                      watch("sameAsInsured") ||
                      watch("unKnownFullName") ||
                      isDisabled
                    }
                    {...FACStepOneRegister("lastName")}
                  />
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-normal">
                      <input
                        id={`${uuid}-unKnownLastName`}
                        type="checkbox"
                        value="unKnownLastName"
                        disabled={isDisabled}
                        className="mr-1"
                        {...FACStepOneRegister("unKnownLastName")}
                      />
                      {t("UnauthFACStep-uknown")}
                    </label>
                  </div>
                </div>

                <div>
                  <label htmlFor={`${uuid}-mn`} className="block mb-1">
                    {t("UnauthFACStep-mi")}
                  </label>
                  <input
                    id={`${uuid}-mn`}
                    type="text"
                    disabled={
                      watch("sameAsInsured") ||
                      watch("unKnownFullName") ||
                      isDisabled
                    }
                    {...FACStepOneRegister("middleName")}
                  />
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr,1fr]">
                <div>
                  <label htmlFor={`${uuid}-dob`} className="block mb-1">
                    {t("UnauthFACStep-dob")}
                  </label>
                  <input
                    id={`${uuid}-dob`}
                    type="date"
                    disabled={watch("unknownDob") || isDisabled}
                    max={moment()
                      .add(-1, "days")
                      .format("YYYY-MM-DD")
                      .toString()}
                    {...FACStepOneRegister("dob")}
                    onKeyUp={() => {
                      clearTimeout(debounceFunction);
                      debounceFunction = setTimeout(async () => {
                        console.log(getValues("dob"));
                        if (
                          getValues("dob") &&
                          moment(getValues("dob")).format("YYYY-MM-DD") >=
                            moment().format("YYYY-MM-DD")
                        )
                          setIsDateOfBirthValid(false);
                        else setIsDateOfBirthValid(true);
                      }, debounceTimer);
                    }}
                    onKeyDown={() => {
                      clearTimeout(debounceFunction);
                    }}
                  />
                  {!isDateOfBirthValid && (
                    <FormInputError
                      errorMessage={"Please select valid date of birth."}
                    />
                  )}
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-normal">
                      <input
                        id={`${uuid}-unknownDob`}
                        type="checkbox"
                        disabled={isDisabled}
                        className="mr-1"
                        {...FACStepOneRegister("unknownDob")}
                      />
                      {t("UnauthFACStep-uknown")}
                    </label>
                  </div>
                </div>

                <div className="relative">
                  <label id={`${uuid}-ssn-label`} className="block mb-1">
                    {t("UnauthFACStep-ssn")}
                  </label>

                  <SSNInput
                    name="ssn"
                    describedby={`${uuid}-ssn-label`}
                    register={FACStepOneRegister}
                    setValue={setValue}
                    length="full"
                    required={false}
                    isDisabled={isDisabled}
                  />
                  <FormInputError
                    id={`${uuid}-ssn-error`}
                    errorMessage={errors.ssn?.message}
                  />
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-normal">
                      <input
                        id={`${uuid}-nknownSnn`}
                        type="checkbox"
                        disabled={isDisabled}
                        className="mr-1"
                        {...FACStepOneRegister("unknownSnn")}
                      />
                      {t("UnauthFACStep-uknown")}
                    </label>
                  </div>
                </div>
                <div>
                  <Controller
                    name="insuredTaxState"
                    control={control}
                    render={() => (
                      <RELEASING_AutoCompleteComboBox
                        labelName={t("UnauthFACStep-state")}
                        inputPlaceholderText={t("UnauthFACStep-state-optn")}
                        options={[
                          {
                            value: "all",
                            displayAs: t("UnauthFACStep-state-optn"),
                            selectedPolicyDisplayAs: t(
                              "UnauthFACStep-state-optn"
                            ),
                            isDisplayOnly: true,
                          },
                          ...states.map((state) => ({
                            value: state.abbreviation,
                            displayAs: state.name,
                            selectedPolicyDisplayAs: state.name,
                            isDisplayOnly: true,
                          })),
                        ]}
                        selectedOption={selectedState}
                        setSelectedOption={(option) => {
                          setSelectedState(option);
                          if (option?.value !== "all") {
                            setValue("insuredTaxState", option.value);
                          } else {
                            setValue("insuredTaxState", "");
                          }
                        }}
                        readOnly={watch("unknownState") || isDisabled}
                      />
                    )}
                  />
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-normal">
                      <input
                        id={`${uuid}-unknownState`}
                        type="checkbox"
                        disabled={isDisabled}
                        className="mr-1"
                        {...FACStepOneRegister("unknownState")}
                      />
                      {t("UnauthFACStep-uknown")}
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,2.05fr]">
                <div>
                  <label htmlFor={`${uuid}-dod`} className="block mb-1">
                    {t("UnauthFACStep-dod")}
                  </label>
                  <input
                    id={`${uuid}-dod`}
                    type="date"
                    max={moment().format("YYYY-MM-DD").toString()}
                    disabled={watch("dodUnknown") || isDisabled}
                    {...FACStepOneRegister("dod")}
                    onKeyUp={() => {
                      clearTimeout(debounceFunction);
                      debounceFunction = setTimeout(async () => {
                        if (
                          getValues("dod") &&
                          moment(getValues("dod")).format("YYYY-MM-DD") >
                            moment().format("YYYY-MM-DD")
                        )
                          setIsDateOfDeathValid(false);
                        else setIsDateOfDeathValid(true);
                      }, debounceTimer);
                    }}
                    onKeyDown={() => {
                      clearTimeout(debounceFunction);
                    }}
                  />
                  {!isDateOfDeathValid && (
                    <FormInputError
                      errorMessage={"Please select valid date of death."}
                    />
                  )}
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-normal">
                      <input
                        id={`${uuid}-unknown`}
                        type="checkbox"
                        disabled={isDisabled}
                        className="mr-1"
                        {...FACStepOneRegister("dodUnknown")}
                      />
                      {t("UnauthFACStep-uknown")}
                    </label>
                  </div>
                </div>
                <div className="mt-4 ">
                  <fieldset>
                    <legend>{t("UnauthFACStep-mod")} </legend>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner1`}
                          type="radio"
                          name="manner"
                          disabled={isDisabled}
                          className="mr-1"
                          value="Natural"
                          {...FACStepOneRegister("manner")}
                        />{" "}
                        {t("UnauthFACStep-label-1")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner2`}
                          type="radio"
                          name="manner"
                          disabled={isDisabled}
                          className="mr-1"
                          value="Accidental"
                          {...FACStepOneRegister("manner")}
                        />{" "}
                        {t("UnauthFACStep-label-2")}
                      </label>
                    </div>

                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner3`}
                          type="radio"
                          disabled={isDisabled}
                          name="manner"
                          className="mr-1"
                          value="other"
                          {...FACStepOneRegister("manner")}
                        />
                        {t("UnauthFACStep-label-3")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner4`}
                          type="radio"
                          name="manner"
                          disabled={isDisabled}
                          className="mr-1"
                          value="Unknown"
                          {...FACStepOneRegister("manner")}
                        />
                        {t("UnauthFACStep-uknown")}
                      </label>
                    </div>
                  </fieldset>
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1.5fr,2.5fr]">
                <div>
                  <fieldset>
                    <legend>{t("UnauthFACStep-dsc")} </legend>

                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-sm font-normal">
                        <input
                          id={`${uuid}-foreign-1`}
                          type="radio"
                          disabled={isDisabled}
                          className="mr-1"
                          value="yes"
                          {...FACStepOneRegister("foreign")}
                        />
                        {t("UnauthFACStep-dsc-label-1")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-sm font-normal">
                        <input
                          id={`${uuid}-foreign-2`}
                          type="radio"
                          disabled={isDisabled}
                          className="mr-1"
                          value="no"
                          {...FACStepOneRegister("foreign")}
                        />
                        {t("UnauthFACStep-dsc-label-2")}
                      </label>
                    </div>
                  </fieldset>
                </div>
                <div>
                  {foreignDeath === "yes" && (
                    <>
                      <label
                        htmlFor={`${uuid}-foreignCountry`}
                        className="block mb-1"
                      >
                        {t("UnauthFACStep-dsc-label-3")}
                      </label>
                      <input
                        disabled={isDisabled}
                        id={`${uuid}-foreignCountry`}
                        {...FACStepOneRegister("foreignCountry")}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr]">
                <div className="">
                  <fieldset>
                    <legend>{t("UnauthFACStep-marital-status")} </legend>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-marital1`}
                          type="radio"
                          name="marital"
                          disabled={isDisabled}
                          className="mr-1"
                          value="married"
                          {...FACStepOneRegister("marital")}
                        />{" "}
                        {t("UnauthFACStep-married")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-marital2`}
                          type="radio"
                          disabled={isDisabled}
                          name="marital"
                          className="mr-1"
                          value="widowed"
                          {...FACStepOneRegister("marital")}
                        />
                        {t("UnauthFACStep-widowed")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-marital3`}
                          type="radio"
                          disabled={isDisabled}
                          name="marital"
                          className="mr-1"
                          value="unknown"
                          {...FACStepOneRegister("marital")}
                        />{" "}
                        {t("UnauthFACStep-single")}
                      </label>
                    </div>
                  </fieldset>
                </div>
                {maritalStatus === "married" && (
                  <div>
                    <label
                      htmlFor={`${uuid}-spouse-name`}
                      className="block mb-1"
                    >
                      {t("UnauthFACStep-nop")}
                    </label>
                    <input
                      disabled={isDisabled}
                      id={`${uuid}-spouse-name`}
                      {...FACStepOneRegister("spouseName")}
                    />
                  </div>
                )}
                {maritalStatus === "widowed" && (
                  <div>
                    <label
                      htmlFor={`${uuid}-widowed-name`}
                      className="block mb-1"
                    >
                      {t("UnauthFACStep-ndp")}
                    </label>
                    <input
                      disabled={isDisabled}
                      id={`${uuid}-widowed-name`}
                      {...FACStepOneRegister("deceasedSpouse")}
                    />
                  </div>
                )}
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                className="ui-btn-primary text-center"
                disabled={isDisabled}
              >
                {t("UnauthFACStep-btn")}
              </button>
            </div>
          </form>
        </EverlakeLoadingOverlay>
      )}
    </>
  );
}
